// @flow
import { type ActionType, createAction, handleActions } from 'redux-actions';
import produce from 'immer';
import * as MeAPI from 'lib/api/me';
import * as PostsAPI from 'lib/api/posts';
import * as ProductsAPI from 'lib/api/products';
import * as BrandsAPI from 'lib/api/brands';
import * as BannersAPI from 'lib/api/banners';
import * as EventsAPI from 'lib/api/events';
import * as LettersAPI from 'lib/api/letters';
import * as QuotesAPI from 'lib/api/quotes';
import * as PartnersAPI from 'lib/api/partners';
import * as SavesAPI from 'lib/api/posts/saves';
import * as SavesProductsAPI from 'lib/api/products/saves';
import * as SavesBrandsAPI from 'lib/api/brands/saves';
import * as SavesBannersAPI from 'lib/api/banners/saves';
import * as SavesEventsAPI from 'lib/api/events/saves';
import * as SavesLettersAPI from 'lib/api/letters/saves';
import * as SavesQuotesAPI from 'lib/api/quotes/saves';
import * as SavesPartnersAPI from 'lib/api/partners/saves';
import * as SeriesAPI from 'lib/api/series';
import format from 'date-fns/format';

import {
  applyPenders,
  convertToPlainText,
  escapeForUrl,
  type GenericResponseAction,
} from 'lib/common';

/* ACTION TYPE */
const EDIT_FIELD = 'write/EDIT_FIELD';
const SET_THUMBNAIL = 'write/SET_THUMBNAIL';
const OPEN_SUBMIT_BOX = 'write/OPEN_SUBMIT_BOX';
const CLOSE_SUBMIT_BOX = 'write/CLOSE_SUBMIT_BOX';
const LIST_CATEGORIES = 'write/LIST_CATEGORIES';
const TOGGLE_CATEGORY = 'write/TOGGLE_CATEGORY';
const INSERT_TAG = 'write/INSERT_TAG';
const REMOVE_TAG = 'write/REMOVE_TAG';
const INSERT_BADGE = 'write/INSERT_BADGE';
const REMOVE_BADGE = 'write/REMOVE_BADGE';
const WRITE_POST = 'write/WRITE_POST';
const WRITE_PRODUCT = 'write/WRITE_PRODUCT';
const WRITE_BRAND = 'write/WRITE_BRAND';
const WRITE_BANNER = 'write/WRITE_BANNER';
const WRITE_EVENT = 'write/WRITE_EVENT';
const WRITE_LETTER = 'write/WRITE_LETTER';
const WRITE_QUOTE = 'write/WRITE_QUOTE';
const WRITE_PARTNER = 'write/WRITE_PARTNER';
const OPEN_CATEGORY_MODAL = 'write/OPEN_CATEGORY_MODAL';
const CLOSE_CATEGORY_MODAL = 'write/CLOSE_CATEOGRY_MODAL';
const CREATE_TEMP_CATEGORY = 'write/CREATE_TEMP_CATEGORY';
const TOGGLE_EDIT_CATEGORY = 'write/TOGGLE_EDIT_CATEGORY';
const CHANGE_CATEGORY_NAME = 'write/CHANGE_CATEGORY_NAME';
const HIDE_CATEGORY = 'write/HIDE_CATEGORY';
const SELECT_CATEGORY1 = 'write/SELECT_CATEGORY1';
const SELECT_CATEGORY2 = 'write/SELECT_CATEGORY2';
const CREATE_CATEGORY = 'write/CREATE_CATEGORY';
const DELETE_CATEGORY = 'write/DELETE_CATERGORY';
const UPDATE_CATEGORY = 'write/UPDATE_CATEGORY';
const REORDER_CATEGORY = 'write/REORDER_CATEGORY';
const REORDER_CATEGORIES = 'write/REORDER_CATEGORIES';
const CHANGE_URL_SLUG = 'write/CHANGE_URL_SLUG';
const UPDATE_POST = 'write/UPDATE_POST';
const UPDATE_PRODUCT = 'write/UPDATE_PRODUCT';
const UPDATE_BRAND = 'write/UPDATE_BRAND';
const UPDATE_BANNER = 'write/UPDATE_BANNER';
const UPDATE_EVENT = 'write/UPDATE_EVENT';
const UPDATE_LETTER = 'write/UPDATE_LETTER';
const UPDATE_QUOTE = 'write/UPDATE_QUOTE';
const UPDATE_PARTNER = 'write/UPDATE_PARTNER';
const RESET = 'write/RESET';
const TEMP_SAVE = 'write/TEMP_SAVE';
const TEMP_SAVE_PRODUCT = 'write/TEMP_SAVE_PRODUCT';
const TEMP_SAVE_BRAND = 'write/TEMP_SAVE_BRAND';
const TEMP_SAVE_BANNER = 'write/TEMP_SAVE_BANNER';
const TEMP_SAVE_EVENT = 'write/TEMP_SAVE_EVENT';
const TEMP_SAVE_LETTER = 'write/TEMP_SAVE_LETTER';
const TEMP_SAVE_QUOTE = 'write/TEMP_SAVE_QUOTE';
const TEMP_SAVE_PARTNER = 'write/TEMP_SAVE_PARTNER';
const SET_UPLOAD_MASK = 'write/SET_UPLOAD_MASK';
const SET_TEMP_DATA = 'write/SET_TEMP_DATA';
const SET_INSERT_TEXT = 'write/SET_INSERT_TEXT';
const SET_UPLOAD_STATUS = 'write/SET_UPLOAD_STATUS';
const SET_UPLOAD_PROGRESS = 'write/SET_UPLOAD_PROGRESS';
const CREATE_UPLOAD_URL = 'write/CREATE_UPLOAD_URL';
const CREATE_PRODUCT_UPLOAD_URL = 'write/CREATE_PRODUCT_UPLOAD_URL';
const CREATE_BANNER_UPLOAD_URL = 'write/CREATE_BANNER_UPLOAD_URL';
const CREATE_BRAND_UPLOAD_URL = 'write/CREATE_BRAND_UPLOAD_URL';
const CREATE_EVENT_UPLOAD_URL = 'write/CREATE_EVENT_UPLOAD_URL';
const CREATE_LETTER_UPLOAD_URL = 'write/CREATE_LETTER_UPLOAD_URL';
const CREATE_QUOTE_UPLOAD_URL = 'write/CREATE_QUOTE_UPLOAD_URL';
const CREATE_PARTNER_UPLOAD_URL = 'write/CREATE_PARTNER_UPLOAD_URL';
const SET_VISIBILITY = 'write/SET_VISIBILITY';

const SHOW_WRITE_EXTRA = 'write/SHOW_WRITE_EXTRA';
const HIDE_WRITE_EXTRA = 'write/HIDE_WRITE_EXTRA';
const SET_LAYOUT_MODE = 'write/SET_LAYOUT_MODE';

const TOGGLE_ADDITIONAL_CONFIG = 'write/TOGGLE_ADDITIONAL_CONFIG';
const SET_META_VALUE = 'write/SET_META_VALUE';
const RESET_META = 'write/RESET_META';

const LIST_TEMP_SAVES = 'write/LIST_TEMP_SAVES';
const LIST_TEMP_SAVES_PRODUCTS = 'write/LIST_TEMP_SAVES_PRODUCTS';
const LIST_TEMP_SAVES_BRANDS = 'write/LIST_TEMP_SAVES_BRANDS';
const LIST_TEMP_SAVES_BANNERS = 'write/LIST_TEMP_SAVES_BANNERS';
const LIST_TEMP_SAVES_EVENTS = 'write/LIST_TEMP_SAVES_EVENTS';
const LIST_TEMP_SAVES_LETTERS = 'write/LIST_TEMP_SAVES_LETTERS';
const LIST_TEMP_SAVES_QUOTES = 'write/LIST_TEMP_SAVES_QUOTES';
const LIST_TEMP_SAVES_PARTNERS = 'write/LIST_TEMP_SAVES_PARTNERS';

const LOAD_TEMP_SAVE = 'write/LOAD_TEMP_SAVE';
const LOAD_TEMP_SAVE_PRODUCT = 'write/LOAD_TEMP_SAVE_PRODUCT';
const LOAD_TEMP_SAVE_BRAND = 'write/LOAD_TEMP_SAVE_BRAND';
const LOAD_TEMP_SAVE_BANNER = 'write/LOAD_TEMP_SAVE_BANNER';
const LOAD_TEMP_SAVE_EVENT = 'write/LOAD_TEMP_SAVE_EVENT';
const LOAD_TEMP_SAVE_LETTER = 'write/LOAD_TEMP_SAVE_LETTER';
const LOAD_TEMP_SAVE_QUOTE = 'write/LOAD_TEMP_SAVE_QUOTE';
const LOAD_TEMP_SAVE_PARTNER = 'write/LOAD_TEMP_SAVE_PARTNER';

const GET_POST_BY_ID = 'write/GET_POST_BY_ID';
const GET_PRODUCT_BY_ID = 'write/GET_PRODUCT_BY_ID';
const GET_BRAND_BY_ID = 'write/GET_BRAND_BY_ID';
const GET_BANNER_BY_ID = 'write/GET_BANNER_BY_ID';
const GET_EVENT_BY_ID = 'write/GET_EVENT_BY_ID';
const GET_LETTER_BY_ID = 'write/GET_LETTER_BY_ID';
const GET_QUOTE_BY_ID = 'write/GET_QUOTE_BY_ID';
const GET_PARTNER_BY_ID = 'write/GET_PARTNER_BY_ID';

const TOGGLE_SERIES_MODE = 'write/TOGGLE_SERIES_MODE';
const CREATE_SERIES = 'write/CREATE_SERIES';
const GET_SERIES_LIST = 'write/GET_SERIES_LIST';
const SELECT_SERIES = 'write/SELECT_SERIES';

let tempCategoryId = 0;

/* ACTION CREATOR */
type EditFieldPayload = { field: string, value: string };
type CreateTempCategoryPayload = { order: number, parent: string };
type ChangeCategoryNamePayload = { id: string, name: string, order: number, parent: string };
type ReorderCategoryPayload = { from: number, to: number };
type SetMetaValuePayload = { name: string, value: string };

/* ACTION CREATORS INTERFACE */
export interface WriteActionCreators {
  editField(payload: EditFieldPayload): any;

  setThumbnail(url: ?string): any;

  openSubmitBox(): any;

  closeSubmitBox(): any;

  listCategories(): any;

  toggleCategory(id: string): any;

  insertTag(tag: string): any;

  removeTag(tag: string): any;

  insertBadge(badge: string): any;

  removeBadge(badge: string): any;

  writePost(payload: PostsAPI.WritePostPayload): any;

  writeProduct(payload: ProductsAPI.WriteProductPayload): any;

  writeBrand(payload: BrandsAPI.WriteBrandPayload): any;

  writeBanner(payload: BannersAPI.WriteBannerPayload): any;

  writeEvent(payload: EventsAPI.WriteEventPayload): any;

  writeLetter(payload: LettersAPI.WriteLetterPayload): any;

  writeQuote(payload: QuotesAPI.WriteQuotePayload): any;

  writePartner(payload: PartnersAPI.WritePartnerPayload): any;

  openCategoryModal(): any;

  closeCategoryModal(): any;

  createTempCategory(payload: CreateTempCategoryPayload): any;

  toggleEditCategory(id: string): any;

  changeCategoryName(payload: ChangeCategoryNamePayload): any;

  hideCategory(id: string): any;

  selectCategory1(id: string): any;

  selectCategory2(id: string): any;

  createCategory(name: string, order: number, parent: string, id: string): any;

  deleteCategory(id: string): any;

  updateCategory(payload: MeAPI.UpdateCategoryPayload): any;

  reorderCategory(payload: ReorderCategoryPayload): any;

  reorderCategories(categoryOrders: MeAPI.ReorderCategoryPayload): any;

  changeUrlSlug(payload: string): any;

  updatePost(payload: PostsAPI.UpdatePostPayload): any;

  updateProduct(payload: ProductsAPI.UpdateProductPayload): any;

  updateBrand(payload: BrandsAPI.UpdateBrandPayload): any;

  updateBanner(payload: BannersAPI.UpdateBannerPayload): any;

  updateEvent(payload: EventsAPI.UpdateEventPayload): any;

  updateLetter(payload: LettersAPI.UpdateLetterPayload): any;

  updateQuote(payload: QuotesAPI.UpdateQuotePayload): any;

  updatePartner(payload: PartnersAPI.UpdatePartnerPayload): any;

  reset(): any;

  tempSave(payload: PostsAPI.TempSavePayload): any;

  tempSaveProduct(payload: ProductsAPI.TempSavePayload): any;

  tempSaveBrand(payload: BrandsAPI.TempSavePayload): any;

  tempSaveBanner(payload: BannersAPI.TempSavePayload): any;

  tempSaveEvent(payload: EventsAPI.TempSavePayload): any;

  tempSaveLetter(payload: LettersAPI.TempSavePayload): any;

  tempSaveQuote(payload: QuotesAPI.TempSavePayload): any;

  tempSavePartner(payload: PartnersAPI.TempSavePayload): any;

  setUploadMask(visible: boolean): any;

  setTempData(): any;

  setInsertText(text: ?string): any;

  setUploadStatus(uploading: boolean): any;

  setUploadProgress(progress: number): any;

  createUploadUrl(payload: PostsAPI.CreateUploadUrlPayload): any;

  createProductUploadUrl(payload: ProductsAPI.CreateUploadUrlPayload): any;

  createBrandUploadUrl(payload: BrandsAPI.CreateUploadUrlPayload): any;

  createBannerUploadUrl(payload: BannersAPI.CreateUploadUrlPayload): any;

  createEventUploadUrl(payload: EventsAPI.CreateUploadUrlPayload): any;

  createLetterUploadUrl(payload: LettersAPI.CreateUploadUrlPayload): any;

  createQuoteUploadUrl(payload: QuotesAPI.CreateUploadUrlPayload): any;

  createPartnerUploadUrl(payload: PartnersAPI.CreateUploadUrlPayload): any;

  setVisibility(isPrivate: boolean): any;

  showWriteExtra(): any;

  hideWriteExtra(): any;

  setLayoutMode(mode: string): any;

  toggleAdditionalConfig(): any;

  setMetaValue(payload: SetMetaValuePayload): any;

  resetMeta(): any;

  listTempSaves(postId: string): any;

  listTempSavesProducts(productId: string): any;

  listTempSavesBrands(brandId: string): any;

  listTempSavesBanners(bannerId: string): any;

  listTempSavesEvents(eventId: string): any;

  listTempSavesLetters(letterId: string): any;

  listTempSavesQuotes(quoteId: string): any;

  listTempSavesPartners(partnerId: string): any;

  loadTempSave(payload: SavesAPI.LoadTempSavePayload): any;

  loadTempSaveProduct(payload: SavesProductsAPI.LoadTempSavePayload): any;

  loadTempSaveBrand(payload: SavesBrandsAPI.LoadTempSavePayload): any;

  loadTempSaveBanner(payload: SavesBannersAPI.LoadTempSavePayload): any;

  loadTempSaveEvent(payload: SavesEventsAPI.LoadTempSavePayload): any;

  loadTempSaveLetter(payload: SavesLettersAPI.LoadTempSavePayload): any;

  loadTempSaveQuote(payload: SavesQuotesAPI.LoadTempSavePayload): any;

  loadTempSavePartner(payload: SavesPartnersAPI.LoadTempSavePayload): any;

  getPostById(postId: string): any;

  getProductById(productId: string): any;

  getBrandById(brandId: string): any;

  getBannerById(bannerId: string): any;

  getEventById(eventId: string): any;

  getLetterById(letterId: string): any;

  getQuoteById(QuoteId: string): any;

  getPartnerById(partnerId: string): any;

  toggleSeriesMode(): any;

  createSeries(payload: SeriesAPI.CreateSeriesPayload): any;

  getSeriesList(username: string): any;

  selectSeries(payload: ?{ id: string, name: string }): any;
}

/* EXPORT ACTION CREATORS */
export const actionCreators = {
  editField: createAction(EDIT_FIELD, (payload: EditFieldPayload) => payload),
  setThumbnail: createAction(SET_THUMBNAIL, (payload: ?string) => payload),
  openSubmitBox: createAction(OPEN_SUBMIT_BOX),
  closeSubmitBox: createAction(CLOSE_SUBMIT_BOX),
  listCategories: createAction(LIST_CATEGORIES, MeAPI.listCategories),
  toggleCategory: createAction(TOGGLE_CATEGORY, (id: string) => id),
  insertTag: createAction(INSERT_TAG, (tag: string) => tag),
  removeTag: createAction(REMOVE_TAG, (tag: string) => tag),
  insertBadge: createAction(INSERT_BADGE, (badge: string) => badge),
  removeBadge: createAction(REMOVE_BADGE, (badge: string) => badge),
  writePost: createAction(WRITE_POST, PostsAPI.writePost),
  writeProduct: createAction(WRITE_PRODUCT, ProductsAPI.writeProduct),
  writeBrand: createAction(WRITE_BRAND, BrandsAPI.writeBrand),
  writeBanner: createAction(WRITE_BANNER, BannersAPI.writeBanner),
  writeEvent: createAction(WRITE_EVENT, EventsAPI.writeEvent),
  writeLetter: createAction(WRITE_LETTER, LettersAPI.writeLetter),
  writeQuote: createAction(WRITE_QUOTE, QuotesAPI.writeQuote),
  writePartner: createAction(WRITE_PARTNER, PartnersAPI.writePartner),
  openCategoryModal: createAction(OPEN_CATEGORY_MODAL),
  closeCategoryModal: createAction(CLOSE_CATEGORY_MODAL),
  createTempCategory: createAction(
    CREATE_TEMP_CATEGORY,
    (payload: CreateTempCategoryPayload) => payload,
  ),
  toggleEditCategory: createAction(TOGGLE_EDIT_CATEGORY, id => id),
  changeCategoryName: createAction(
    CHANGE_CATEGORY_NAME,
    (payload: ChangeCategoryNamePayload) => payload,
  ),
  hideCategory: createAction(HIDE_CATEGORY, id => id),
  selectCategory1: createAction(SELECT_CATEGORY1, id => id),
  selectCategory2: createAction(SELECT_CATEGORY2, id => id),
  createCategory: createAction(
    CREATE_CATEGORY,
    MeAPI.createCategory,
    (name, order, parent, id) => id,
  ),
  deleteCategory: createAction(DELETE_CATEGORY, MeAPI.deleteCategory),
  updateCategory: createAction(UPDATE_CATEGORY, MeAPI.updateCategory),
  reorderCategory: createAction(REORDER_CATEGORY),
  reorderCategories: createAction(REORDER_CATEGORIES, MeAPI.reorderCategories),
  changeUrlSlug: createAction(CHANGE_URL_SLUG, urlSlug => urlSlug),
  updatePost: createAction(UPDATE_POST, PostsAPI.updatePost),
  updateProduct: createAction(UPDATE_PRODUCT, ProductsAPI.updateProduct),
  updateBrand: createAction(UPDATE_BRAND, BrandsAPI.updateBrand),
  updateBanner: createAction(UPDATE_BANNER, BannersAPI.updateBanner),
  updateEvent: createAction(UPDATE_EVENT, EventsAPI.updateEvent),
  updateLetter: createAction(UPDATE_LETTER, LettersAPI.updateLetter),
  updateQuote: createAction(UPDATE_QUOTE, QuotesAPI.updateQuote),
  updatePartner: createAction(UPDATE_PARTNER, PartnersAPI.updatePartner),
  reset: createAction(RESET),
  tempSave: createAction(TEMP_SAVE, PostsAPI.tempSave),
  tempSaveProduct: createAction(TEMP_SAVE_PRODUCT, ProductsAPI.tempSave),
  tempSaveBrand: createAction(TEMP_SAVE_BRAND, BrandsAPI.tempSave),
  tempSaveBanner: createAction(TEMP_SAVE_BANNER, BannersAPI.tempSave),
  tempSaveEvent: createAction(TEMP_SAVE_EVENT, EventsAPI.tempSave),
  tempSaveLetter: createAction(TEMP_SAVE_LETTER, LettersAPI.tempSave),
  tempSaveQuote: createAction(TEMP_SAVE_QUOTE, QuotesAPI.tempSave),
  tempSavePartner: createAction(TEMP_SAVE_PARTNER, PartnersAPI.tempSave),
  setUploadMask: createAction(SET_UPLOAD_MASK, (visible: boolean) => visible),
  setTempData: createAction(SET_TEMP_DATA, () => {
    const now = format(new Date(), 'YYYY-MM-DD HH:MM');
    const tempText = `${now} 등록됨`;
    return tempText;
  }),
  setInsertText: createAction(SET_INSERT_TEXT, (text: ?string) => text),
  setUploadStatus: createAction(SET_UPLOAD_STATUS, (uploading: boolean) => uploading),
  setUploadProgress: createAction(SET_UPLOAD_PROGRESS, (progress: number) => progress),
  createUploadUrl: createAction(CREATE_UPLOAD_URL, PostsAPI.createUploadUrl),
  createProductUploadUrl: createAction(CREATE_PRODUCT_UPLOAD_URL, ProductsAPI.createUploadUrl),
  createBannerUploadUrl: createAction(CREATE_BANNER_UPLOAD_URL, BannersAPI.createUploadUrl),
  createBrandUploadUrl: createAction(CREATE_BRAND_UPLOAD_URL, BrandsAPI.createUploadUrl),
  createEventUploadUrl: createAction(CREATE_EVENT_UPLOAD_URL, EventsAPI.createUploadUrl),
  createLetterUploadUrl: createAction(CREATE_LETTER_UPLOAD_URL, LettersAPI.createUploadUrl),
  createQuoteUploadUrl: createAction(CREATE_QUOTE_UPLOAD_URL, QuotesAPI.createUploadUrl),
  createPartnerUploadUrl: createAction(CREATE_PARTNER_UPLOAD_URL, PartnersAPI.createUploadUrl),
  setVisibility: createAction(SET_VISIBILITY, (isPrivate: boolean) => isPrivate),
  showWriteExtra: createAction(SHOW_WRITE_EXTRA),
  hideWriteExtra: createAction(HIDE_WRITE_EXTRA),
  setLayoutMode: createAction(SET_LAYOUT_MODE),
  toggleAdditionalConfig: createAction(TOGGLE_ADDITIONAL_CONFIG),
  setMetaValue: createAction(SET_META_VALUE, (payload: SetMetaValuePayload) => payload),
  resetMeta: createAction(RESET_META),
  listTempSaves: createAction(LIST_TEMP_SAVES, SavesAPI.getTempSaveList),
  listTempSavesProducts: createAction(LIST_TEMP_SAVES_PRODUCTS, SavesProductsAPI.getTempSaveList),
  listTempSavesBrands: createAction(LIST_TEMP_SAVES_BRANDS, SavesBrandsAPI.getTempSaveList),
  listTempSavesBanners: createAction(LIST_TEMP_SAVES_BANNERS, SavesBannersAPI.getTempSaveList),
  listTempSavesEvents: createAction(LIST_TEMP_SAVES_EVENTS, SavesEventsAPI.getTempSaveList),
  listTempSavesLetters: createAction(LIST_TEMP_SAVES_LETTERS, SavesLettersAPI.getTempSaveList),
  listTempSavesQuotes: createAction(LIST_TEMP_SAVES_QUOTES, SavesQuotesAPI.getTempSaveList),
  listTempSavesPartners: createAction(LIST_TEMP_SAVES_PARTNERS, SavesPartnersAPI.getTempSaveList),
  loadTempSave: createAction(LOAD_TEMP_SAVE, SavesAPI.loadTempSave),
  loadTempSaveProduct: createAction(LOAD_TEMP_SAVE_PRODUCT, SavesProductsAPI.loadTempSave),
  loadTempSaveBrand: createAction(LOAD_TEMP_SAVE_BRAND, SavesBrandsAPI.loadTempSave),
  loadTempSaveBanner: createAction(LOAD_TEMP_SAVE_BANNER, SavesBannersAPI.loadTempSave),
  loadTempSaveEvent: createAction(LOAD_TEMP_SAVE_EVENT, SavesEventsAPI.loadTempSave),
  loadTempSaveLetter: createAction(LOAD_TEMP_SAVE_LETTER, SavesLettersAPI.loadTempSave),
  loadTempSaveQuote: createAction(LOAD_TEMP_SAVE_QUOTE, SavesQuotesAPI.loadTempSave),
  loadTempSavePartner: createAction(LOAD_TEMP_SAVE_PARTNER, SavesPartnersAPI.loadTempSave),
  getPostById: createAction(GET_POST_BY_ID, PostsAPI.getPostById),
  getProductById: createAction(GET_PRODUCT_BY_ID, ProductsAPI.getProductById),
  getBrandById: createAction(GET_BRAND_BY_ID, BrandsAPI.getBrandById),
  getBannerById: createAction(GET_BANNER_BY_ID, BannersAPI.getBannerById),
  getEventById: createAction(GET_EVENT_BY_ID, EventsAPI.getEventById),
  getLetterById: createAction(GET_LETTER_BY_ID, LettersAPI.getLetterById),
  getQuoteById: createAction(GET_QUOTE_BY_ID, QuotesAPI.getQuoteById),
  getPartnerById: createAction(GET_PARTNER_BY_ID, PartnersAPI.getPartnerById),
  toggleSeriesMode: createAction(TOGGLE_SERIES_MODE),
  createSeries: createAction(CREATE_SERIES, SeriesAPI.createSeries),
  getSeriesList: createAction(GET_SERIES_LIST, SeriesAPI.getSeriesList),
  selectSeries: createAction(SELECT_SERIES, (payload: ?{ id: string, name: string }) => payload),
};

export type BriefTempSaveInfo = {
  id: string,
  created_at: string,
  title: string,
};

export type TempSaveData = BriefTempSaveInfo & {
  body: string,
};

/* STATE TYPES */
export type Category = {
  id: string,
  order: number,
  parent: string,
  private: boolean,
  name: string,
  urlSlug: string,
  active: boolean,
  temp?: boolean,
  edit?: boolean,
  hide?: boolean,
  edited?: boolean,
};
export type Categories = Category[];
export type SubmitBox = {
  open: boolean,
  tags: string[],
  badge: string[],
  categories: ?Categories,
  additional: boolean,
  url_slug: ?string,
  is_private: boolean,
  series: ?{
    id: string,
    name: string,
  },
};
export type CategoryModal = {
  open: boolean,
  categories: ?Categories,
  selectCategory1: ?Categories,
  selectCategory2: ?Categories,
  ordered: boolean,
};

export type Meta = {
  code_theme?: string,
  short_description?: ?string,
  price?: ?string,
  supplier?: ?string,
  productUrl?: ?string,
  partnerUrl?: ?string,
  brandUrl?: ?string,
  quoteStatus?: ?string,
  eventAt?: ?string,
  eventStatus?: ?boolean,
};

export type PostData = {
  id: string,
  title: string,
  body: string,
  thumbnail: string,
  is_markdown: boolean,
  is_temp: boolean,
  created_at: string,
  updated_at: string,
  tags: string[],
  badges: string[],
  categories: {
    id: string,
    name: string,
    order: number,
    parent: string,
  }[],
  url_slug: string,
  meta: Meta,
  is_private: boolean,
  series: ?{
    id: string,
    name: string,
  },
};

export type Upload = {
  mask: boolean,
  uploading: boolean,
  progress: number,
  uploadUrl: ?string,
  id: ?string,
  imagePath: ?string,
};

export type LayoutMode = 'editor' | 'both' | 'preview';

export type WriteExtra = {
  visible: boolean,
  layoutMode: LayoutMode,
};

export type SeriesItemData = {
  id: string,
  name: string,
  description: string,
  thumbnail: string,
  url_slug: string,
  created_at: string,
  updated_at: string,
  user: {
    username: string,
    thumbnail: string,
  },
};

export type SeriesModal = {
  visible: boolean,
  list: ?(SeriesItemData[]),
};

export type Write = {
  body: string,
  title: string,
  thumbnail: ?string,
  meta: Meta,
  submitBox: SubmitBox,
  postData: ?PostData,
  categoryModal: CategoryModal,
  upload: Upload,
  insertText: ?string,
  writeExtra: WriteExtra,
  tempSaves: ?(BriefTempSaveInfo[]),
  changed: boolean,
  seriesModal: SeriesModal,
};

/* ACTION FLOW TYPE */
type EditFieldAction = ActionType<typeof actionCreators.editField>;
type CreateTempCategoryAction = ActionType<typeof actionCreators.createTempCategory>;
type SetThumbnailAction = ActionType<typeof actionCreators.setThumbnail>;
type ToggleCategoryAction = ActionType<typeof actionCreators.toggleCategory>;
type InsertTagAction = ActionType<typeof actionCreators.insertTag>;
type RemoveTagAction = ActionType<typeof actionCreators.removeTag>;
type InsertBadgeAction = ActionType<typeof actionCreators.insertBadge>;
type RemoveBadgeAction = ActionType<typeof actionCreators.removeBadge>;
type ToggleEditCategoryAction = ActionType<typeof actionCreators.toggleEditCategory>;
type ChangeCategoryNameAction = ActionType<typeof actionCreators.changeCategoryName>;
type HideCategoryAction = ActionType<typeof actionCreators.hideCategory>;
type SelectCategory1Action = ActionType<typeof actionCreators.selectCategory1>;
type SelectCategory2Action = ActionType<typeof actionCreators.selectCategory2>;
type ReorderCategoryAction = ActionType<typeof actionCreators.reorderCategory>;
type SetUploadMaskAction = ActionType<typeof actionCreators.setUploadMask>;
type SetTempDataAction = ActionType<typeof actionCreators.setTempData>;
type SetMetaValueAction = ActionType<typeof actionCreators.setMetaValue>;
type ListTempSavesResponseAction = GenericResponseAction<BriefTempSaveInfo[], null>;
type LoadTempSaveResponseAction = GenericResponseAction<TempSaveData, null>;
type GetPostByIdResponseAction = GenericResponseAction<PostData, null>;
type ChangeUrlSlugAction = ActionType<typeof actionCreators.changeUrlSlug>;
type SetVisibilityAction = ActionType<typeof actionCreators.setVisibility>;

type GetSeriesResponseAction = GenericResponseAction<SeriesItemData[], null>;
type SelectSeriesAction = ActionType<typeof actionCreators.selectSeries>;

const initialState: Write = {
  body: '',
  thumbnail: null,
  title: '',
  meta: {
    code_theme: '',
    short_description: null,
  },
  submitBox: {
    open: false,
    categories: null,
    tags: [],
    badges: [],
    additional: false,
    url_slug: null,
    is_private: false,
    series: null,
  },
  postData: null,
  categoryModal: {
    open: false,
    categories: null,
    selectCategory1: null,
    selectCategory2: null,
    ordered: false,
  },
  upload: {
    mask: false,
    uploading: false,
    progress: 0,
    uploadUrl: null,
    imagePath: null,
    id: null,
  },
  writeExtra: {
    visible: false,
    layoutMode: 'both',
  },
  insertText: null,
  tempSaves: null,
  changed: false,
  seriesModal: {
    visible: false,
    list: null,
  },
};

const reducer = handleActions(
  {
    [EDIT_FIELD]: (state, { payload }: EditFieldAction) => {
      const { field, value } = payload;
      return produce(state, draft => {
        draft[field] = value;
        draft.changed = true;
      });
    },
    // $FlowFixMe
    [SET_THUMBNAIL]: (state, { payload }: SetThumbnailAction) => {
      return produce(state, draft => {
        draft.thumbnail = payload;
      });
    },
    [OPEN_SUBMIT_BOX]: state => {
      return produce(state, draft => {
        draft.submitBox.open = true;
        draft.submitBox.additional = false;
        draft.seriesModal.visible = false;
      });
    },
    [CLOSE_SUBMIT_BOX]: state =>
      produce(state, draft => {
        draft.submitBox.open = false;
      }),
    [TOGGLE_CATEGORY]: (state, { payload: id }: ToggleCategoryAction) => {
      if (!state.submitBox.categories) return state;
      const index = state.submitBox.categories.findIndex(category => category.id === id);
      return produce(state, draft => {
        if (!draft.submitBox.categories) return;
        draft.submitBox.categories[index].active = !draft.submitBox.categories[index].active;
      });
    },
    [INSERT_TAG]: (state, { payload: tag }: InsertTagAction) => {
      return produce(state, draft => {
        draft.submitBox.tags.push(tag);
      });
    },
    [REMOVE_TAG]: (state, { payload: tag }: RemoveTagAction) => {
      return produce(state, draft => {
        draft.submitBox.tags = draft.submitBox.tags.filter(t => t !== tag);
      });
    },
    [INSERT_BADGE]: (state, { payload: badge }: InsertBadgeAction) => {
      return produce(state, draft => {
        draft.submitBox.badges.push(badge);
      });
    },
    [REMOVE_BADGE]: (state, { payload: badge }: RemoveBadgeAction) => {
      return produce(state, draft => {
        draft.submitBox.badges = draft.submitBox.badges.filter(t => t !== badge);
      });
    },
    [OPEN_CATEGORY_MODAL]: state => {
      return produce(state, draft => {
        draft.categoryModal.open = true;
        draft.categoryModal.categories = state.submitBox.categories;
        draft.categoryModal.ordered = false;
      });
    },
    [CLOSE_CATEGORY_MODAL]: state => {
      return produce(state, draft => {
        draft.categoryModal.open = false;
      });
    },
    [CREATE_TEMP_CATEGORY]: (state, { payload }: CreateTempCategoryAction) => {
      const { order, parent } = payload;
      return produce(state, draft => {
        tempCategoryId += 1;
        const tempCategory: Category = {
          id: tempCategoryId.toString(),
          order,
          parent,
          private: false,
          name: '',
          urlSlug: '',
          active: false,
          edit: true,
          temp: true,
          edited: false,
          hide: false,
        };
        if (!draft.categoryModal.categories) return;
        draft.categoryModal.categories.push(tempCategory);
      });
    },
    [TOGGLE_EDIT_CATEGORY]: (state, { payload: id }: ToggleEditCategoryAction) => {
      if (!state.categoryModal.categories) return state;
      const index = state.categoryModal.categories.findIndex(c => c.id === id);
      return produce(state, draft => {
        if (!draft.categoryModal.categories) return;
        const category = draft.categoryModal.categories[index];
        category.edit = !category.edit;
        category.edited = true;
      });
    },
    [CHANGE_CATEGORY_NAME]: (
      state,
      { payload: { id, name, order, parent } }: ChangeCategoryNameAction,
    ) => {
      if (!state.categoryModal.categories) return state;
      const index = state.categoryModal.categories.findIndex(c => c.id === id);
      return produce(state, draft => {
        if (!draft.categoryModal.categories) return;
        if (name) draft.categoryModal.categories[index].name = name;
        if (order) draft.categoryModal.categories[index].order = order;
        if (parent) draft.categoryModal.categories[index].parent = parent;
      });
    },
    [HIDE_CATEGORY]: (state, { payload: id }: HideCategoryAction) => {
      if (!state.categoryModal.categories) return state;
      const index = state.categoryModal.categories.findIndex(c => c.id === id);
      return produce(state, draft => {
        if (!draft.categoryModal.categories) return;
        draft.categoryModal.categories[index].hide = true;
      });
    },
    [SELECT_CATEGORY1]: (state, { payload: id }: SelectCategory1Action) => {
      return produce(state, draft => {
        draft.categoryModal.selectCategory1 = id;
        draft.categoryModal.selectCategory2 = null;
      });
    },
    [SELECT_CATEGORY2]: (state, { payload: id }: SelectCategory2Action) => {
      return produce(state, draft => {
        draft.categoryModal.selectCategory2 = id;
      });
    },
    [REORDER_CATEGORY]: (state, { payload: { from, to } }: ReorderCategoryAction) => {
      if (!state.categoryModal.categories) return state;
      const fromItem = state.categoryModal.categories[from];
      return produce(state, draft => {
        if (!draft.categoryModal.categories) return;
        draft.categoryModal.categories.splice(from, 1);

        if (!draft.categoryModal.categories) return;
        draft.categoryModal.categories.splice(to, 0, fromItem);
        draft.categoryModal.ordered = true;
      });
    },
    [RESET]: () => {
      // resets the state (when leaves write page)
      return initialState;
    },
    [SET_UPLOAD_MASK]: (state, { payload: visible }: SetUploadMaskAction) => {
      return produce(state, draft => {
        draft.upload.mask = visible;
      });
    },
    [SET_TEMP_DATA]: (state, action: SetTempDataAction) => {
      return produce(state, draft => {
        if (state.body === '') {
          draft.body = action.payload;
        }
        if (state.title === '') {
          draft.title = action.payload;
        }
      });
    },
    [SET_INSERT_TEXT]: (state, action) => {
      return produce(state, draft => {
        draft.insertText = action.payload;
      });
    },
    [SET_UPLOAD_STATUS]: (state, action) => {
      return produce(state, draft => {
        draft.upload.uploading = action.payload;
      });
    },
    [SET_UPLOAD_PROGRESS]: (state, action) => {
      return produce(state, draft => {
        draft.upload.progress = action.payload;
      });
    },
    [SHOW_WRITE_EXTRA]: (state, action) => {
      return produce(state, draft => {
        draft.writeExtra.visible = true;
      });
    },
    [HIDE_WRITE_EXTRA]: state => {
      return produce(state, draft => {
        draft.writeExtra.visible = false;
      });
    },
    [SET_LAYOUT_MODE]: (state, action) => {
      return produce(state, draft => {
        draft.writeExtra.layoutMode = action.payload;
      });
    },
    [TOGGLE_ADDITIONAL_CONFIG]: state => {
      return produce(state, draft => {
        if (!state.submitBox.additional) {
          // put default values
          if (state.postData) {
            draft.meta = { ...state.postData.meta };
          }
          if (!draft.meta.short_description) {
            draft.meta.short_description = convertToPlainText(state.body);
          }
          if (!draft.submitBox.url_slug) {
            draft.submitBox.url_slug =
              (state.postData && state.postData.url_slug) || escapeForUrl(state.title);
          }
        }
        draft.submitBox.additional = !state.submitBox.additional;
      });
    },
    [SET_META_VALUE]: (state, { payload }: SetMetaValueAction) => {
      return produce(state, draft => {
        draft.meta[payload.name] = payload.value;
      });
    },
    [RESET_META]: state => {
      return {
        ...state,
        meta: initialState.meta,
      };
    },
    [CHANGE_URL_SLUG]: (state, { payload }: ChangeUrlSlugAction) => {
      return produce(state, draft => {
        draft.submitBox.url_slug = payload;
      });
    },
    [SET_VISIBILITY]: (state, { payload }: SetVisibilityAction) => {
      return produce(state, draft => {
        draft.submitBox.is_private = payload;
      });
    },
    [TOGGLE_SERIES_MODE]: state =>
      produce(state, draft => {
        draft.seriesModal.visible = !draft.seriesModal.visible;
      }),
    // $FlowFixMe
    [SELECT_SERIES]: (state, { payload }: SelectSeriesAction) => {
      return produce(state, draft => {
        draft.submitBox.series = payload;
      });
    },
  },
  initialState,
);

export default applyPenders(reducer, [
  {
    type: LIST_CATEGORIES,
    onSuccess: (state: Write, { payload: { data } }) => {
      const categories: Categories = data.map(category => ({
        id: category.id,
        order: category.order,
        parent: category.parent,
        private: category.private,
        name: category.name,
        urlSlug: category.url_slug,
      }));
      return produce(state, draft => {
        draft.submitBox.categories = categories;
        // turn on active categories (created post)
        if (state.postData) {
          const categoryIds = state.postData.categories.map(c => c.id);
          if (!draft.submitBox.categories) return;
          draft.submitBox.categories = draft.submitBox.categories.map(c => ({
            ...c,
            active: categoryIds.indexOf(c.id) !== -1,
          }));
        }
        // existing active categories
        if (state.submitBox.categories && state.submitBox.categories.length > 0) {
          const categoryIds = state.submitBox.categories.filter(c => c.active).map(c => c.id);
          if (!draft.submitBox.categories) return;
          draft.submitBox.categories = draft.submitBox.categories.map(c =>
            categoryIds.indexOf(c.id) !== -1 ? { ...c, active: true } : c,
          );
        }
      });
    },
  },
  {
    type: WRITE_POST,
    onSuccess: (state: Write, { payload: { data } }) => {
      if (!data) return state;
      return produce(state, draft => {
        draft.changed = false;
        draft.postData = data;
        draft.submitBox.url_slug = data.url_slug;
      });
    },
  },
  {
    type: WRITE_PRODUCT,
    onSuccess: (state: Write, { payload: { data } }) => {
      if (!data) return state;
      return produce(state, draft => {
        draft.changed = false;
        draft.postData = data;
        draft.submitBox.url_slug = data.url_slug;
      });
    },
  },
  {
    type: WRITE_BRAND,
    onSuccess: (state: Write, { payload: { data } }) => {
      if (!data) return state;
      return produce(state, draft => {
        draft.changed = false;
        draft.postData = data;
        draft.submitBox.url_slug = data.url_slug;
      });
    },
  },
  {
    type: WRITE_BANNER,
    onSuccess: (state: Write, { payload: { data } }) => {
      if (!data) return state;
      return produce(state, draft => {
        draft.changed = false;
        draft.postData = data;
        draft.submitBox.url_slug = data.url_slug;
      });
    },
  },
  {
    type: WRITE_EVENT,
    onSuccess: (state: Write, { payload: { data } }) => {
      if (!data) return state;
      return produce(state, draft => {
        draft.changed = false;
        draft.postData = data;
        draft.submitBox.url_slug = data.url_slug;
      });
    },
  },
  {
    type: WRITE_LETTER,
    onSuccess: (state: Write, { payload: { data } }) => {
      if (!data) return state;
      return produce(state, draft => {
        draft.changed = false;
        draft.postData = data;
        draft.submitBox.url_slug = data.url_slug;
      });
    },
  },
  {
    type: WRITE_QUOTE,
    onSuccess: (state: Write, { payload: { data } }) => {
      if (!data) return state;
      return produce(state, draft => {
        draft.changed = false;
        draft.postData = data;
        draft.submitBox.url_slug = data.url_slug;
      });
    },
  },
  {
    type: WRITE_PARTNER,
    onSuccess: (state: Write, { payload: { data } }) => {
      if (!data) return state;
      return produce(state, draft => {
        draft.changed = false;
        draft.postData = data;
        draft.submitBox.url_slug = data.url_slug;
      });
    },
  },
  {
    type: CREATE_CATEGORY,
    onSuccess: (state: Write, action) => {
      const { payload, meta } = action;
      if (!state.categoryModal.categories) return state;
      const index = state.categoryModal.categories.findIndex(c => c.id === meta);
      return produce(state, draft => {
        if (!draft.categoryModal.categories) return;
        draft.categoryModal.categories[index].id = payload.data.id;
      });
    },
  },
  {
    type: UPDATE_POST,
    onSuccess: (state: Write, { payload: { data } }) => {
      return produce(state, draft => {
        draft.changed = false;
        draft.postData = data;
        draft.submitBox.url_slug = data.url_slug;
      });
    },
  },
  {
    type: UPDATE_PRODUCT,
    onSuccess: (state: Write, { payload: { data } }) => {
      return produce(state, draft => {
        draft.changed = false;
        draft.postData = data;
        draft.submitBox.url_slug = data.url_slug;
      });
    },
  },
  {
    type: UPDATE_BRAND,
    onSuccess: (state: Write, { payload: { data } }) => {
      return produce(state, draft => {
        draft.changed = false;
        draft.postData = data;
        draft.submitBox.url_slug = data.url_slug;
      });
    },
  },
  {
    type: UPDATE_BANNER,
    onSuccess: (state: Write, { payload: { data } }) => {
      return produce(state, draft => {
        draft.changed = false;
        draft.postData = data;
        draft.submitBox.url_slug = data.url_slug;
      });
    },
  },
  {
    type: UPDATE_EVENT,
    onSuccess: (state: Write, { payload: { data } }) => {
      return produce(state, draft => {
        draft.changed = false;
        draft.postData = data;
        draft.submitBox.url_slug = data.url_slug;
      });
    },
  },
  {
    type: UPDATE_LETTER,
    onSuccess: (state: Write, { payload: { data } }) => {
      return produce(state, draft => {
        draft.changed = false;
        draft.postData = data;
        draft.submitBox.url_slug = data.url_slug;
      });
    },
  },
  {
    type: UPDATE_QUOTE,
    onSuccess: (state: Write, { payload: { data } }) => {
      return produce(state, draft => {
        draft.changed = false;
        draft.postData = data;
        draft.submitBox.url_slug = data.url_slug;
      });
    },
  },
  {
    type: UPDATE_PARTNER,
    onSuccess: (state: Write, { payload: { data } }) => {
      return produce(state, draft => {
        draft.changed = false;
        draft.postData = data;
        draft.submitBox.url_slug = data.url_slug;
      });
    },
  },
  {
    type: TEMP_SAVE,
    onSuccess: (state: Write) => {
      return {
        ...state,
        changed: false,
      };
    },
  },
  {
    type: TEMP_SAVE_PRODUCT,
    onSuccess: (state: Write) => {
      return {
        ...state,
        changed: false,
      };
    },
  },
  {
    type: TEMP_SAVE_BRAND,
    onSuccess: (state: Write) => {
      return {
        ...state,
        changed: false,
      };
    },
  },
  {
    type: TEMP_SAVE_BANNER,
    onSuccess: (state: Write) => {
      return {
        ...state,
        changed: false,
      };
    },
  },
  {
    type: TEMP_SAVE_EVENT,
    onSuccess: (state: Write) => {
      return {
        ...state,
        changed: false,
      };
    },
  },
  {
    type: TEMP_SAVE_LETTER,
    onSuccess: (state: Write) => {
      return {
        ...state,
        changed: false,
      };
    },
  },
  {
    type: TEMP_SAVE_QUOTE,
    onSuccess: (state: Write) => {
      return {
        ...state,
        changed: false,
      };
    },
  },
  {
    type: TEMP_SAVE_PARTNER,
    onSuccess: (state: Write) => {
      return {
        ...state,
        changed: false,
      };
    },
  },
  {
    type: CREATE_UPLOAD_URL,
    onSuccess: (state: Write, { payload: { data } }) => {
      return produce(state, draft => {
        draft.upload.uploadUrl = data.url;
        draft.upload.imagePath = data.imagePath;
        draft.upload.id = data.id;
      });
    },
  },
  {
    type: CREATE_PRODUCT_UPLOAD_URL,
    onSuccess: (state: Write, { payload: { data } }) => {
      return produce(state, draft => {
        draft.upload.uploadUrl = data.url;
        draft.upload.imagePath = data.imagePath;
        draft.upload.id = data.id;
      });
    },
  },
  {
    type: CREATE_BANNER_UPLOAD_URL,
    onSuccess: (state: Write, { payload: { data } }) => {
      return produce(state, draft => {
        draft.upload.uploadUrl = data.url;
        draft.upload.imagePath = data.imagePath;
        draft.upload.id = data.id;
      });
    },
  },
  {
    type: CREATE_BRAND_UPLOAD_URL,
    onSuccess: (state: Write, { payload: { data } }) => {
      return produce(state, draft => {
        draft.upload.uploadUrl = data.url;
        draft.upload.imagePath = data.imagePath;
        draft.upload.id = data.id;
      });
    },
  },
  {
    type: CREATE_EVENT_UPLOAD_URL,
    onSuccess: (state: Write, { payload: { data } }) => {
      return produce(state, draft => {
        draft.upload.uploadUrl = data.url;
        draft.upload.imagePath = data.imagePath;
        draft.upload.id = data.id;
      });
    },
  },
  {
    type: CREATE_LETTER_UPLOAD_URL,
    onSuccess: (state: Write, { payload: { data } }) => {
      return produce(state, draft => {
        draft.upload.uploadUrl = data.url;
        draft.upload.imagePath = data.imagePath;
        draft.upload.id = data.id;
      });
    },
  },
  {
    type: CREATE_QUOTE_UPLOAD_URL,
    onSuccess: (state: Write, { payload: { data } }) => {
      return produce(state, draft => {
        draft.upload.uploadUrl = data.url;
        draft.upload.imagePath = data.imagePath;
        draft.upload.id = data.id;
      });
    },
  },
  {
    type: CREATE_PARTNER_UPLOAD_URL,
    onSuccess: (state: Write, { payload: { data } }) => {
      return produce(state, draft => {
        draft.upload.uploadUrl = data.url;
        draft.upload.imagePath = data.imagePath;
        draft.upload.id = data.id;
      });
    },
  },
  {
    type: LIST_TEMP_SAVES,
    onSuccess: (state: Write, { payload }: ListTempSavesResponseAction) => {
      return {
        ...state,
        tempSaves: payload.data,
      };
    },
  },
  {
    type: LIST_TEMP_SAVES_PRODUCTS,
    onSuccess: (state: Write, { payload }: ListTempSavesResponseAction) => {
      return {
        ...state,
        tempSaves: payload.data,
      };
    },
  },
  {
    type: LIST_TEMP_SAVES_BRANDS,
    onSuccess: (state: Write, { payload }: ListTempSavesResponseAction) => {
      return {
        ...state,
        tempSaves: payload.data,
      };
    },
  },
  {
    type: LIST_TEMP_SAVES_BANNERS,
    onSuccess: (state: Write, { payload }: ListTempSavesResponseAction) => {
      return {
        ...state,
        tempSaves: payload.data,
      };
    },
  },
  {
    type: LIST_TEMP_SAVES_EVENTS,
    onSuccess: (state: Write, { payload }: ListTempSavesResponseAction) => {
      return {
        ...state,
        tempSaves: payload.data,
      };
    },
  },
  {
    type: LIST_TEMP_SAVES_LETTERS,
    onSuccess: (state: Write, { payload }: ListTempSavesResponseAction) => {
      return {
        ...state,
        tempSaves: payload.data,
      };
    },
  },
  {
    type: LIST_TEMP_SAVES_QUOTES,
    onSuccess: (state: Write, { payload }: ListTempSavesResponseAction) => {
      return {
        ...state,
        tempSaves: payload.data,
      };
    },
  },
  {
    type: LIST_TEMP_SAVES_PARTNERS,
    onSuccess: (state: Write, { payload }: ListTempSavesResponseAction) => {
      return {
        ...state,
        tempSaves: payload.data,
      };
    },
  },
  {
    type: LOAD_TEMP_SAVE,
    onSuccess: (state: Write, { payload }: LoadTempSaveResponseAction) => {
      return produce(state, draft => {
        draft.changed = false;
        draft.body = payload.data.body;
        draft.title = payload.data.title;
      });
    },
  },
  {
    type: LOAD_TEMP_SAVE_PRODUCT,
    onSuccess: (state: Write, { payload }: LoadTempSaveResponseAction) => {
      return produce(state, draft => {
        draft.changed = false;
        draft.body = payload.data.body;
        draft.title = payload.data.title;
      });
    },
  },
  {
    type: LOAD_TEMP_SAVE_BRAND,
    onSuccess: (state: Write, { payload }: LoadTempSaveResponseAction) => {
      return produce(state, draft => {
        draft.changed = false;
        draft.body = payload.data.body;
        draft.title = payload.data.title;
      });
    },
  },
  {
    type: LOAD_TEMP_SAVE_BANNER,
    onSuccess: (state: Write, { payload }: LoadTempSaveResponseAction) => {
      return produce(state, draft => {
        draft.changed = false;
        draft.body = payload.data.body;
        draft.title = payload.data.title;
      });
    },
  },
  {
    type: LOAD_TEMP_SAVE_EVENT,
    onSuccess: (state: Write, { payload }: LoadTempSaveResponseAction) => {
      return produce(state, draft => {
        draft.changed = false;
        draft.body = payload.data.body;
        draft.title = payload.data.title;
      });
    },
  },
  {
    type: LOAD_TEMP_SAVE_LETTER,
    onSuccess: (state: Write, { payload }: LoadTempSaveResponseAction) => {
      return produce(state, draft => {
        draft.changed = false;
        draft.body = payload.data.body;
        draft.title = payload.data.title;
      });
    },
  },
  {
    type: LOAD_TEMP_SAVE_QUOTE,
    onSuccess: (state: Write, { payload }: LoadTempSaveResponseAction) => {
      return produce(state, draft => {
        draft.changed = false;
        draft.body = payload.data.body;
        draft.title = payload.data.title;
      });
    },
  },
  {
    type: LOAD_TEMP_SAVE_PARTNER,
    onSuccess: (state: Write, { payload }: LoadTempSaveResponseAction) => {
      return produce(state, draft => {
        draft.changed = false;
        draft.body = payload.data.body;
        draft.title = payload.data.title;
      });
    },
  },
  {
    type: GET_POST_BY_ID,
    onSuccess: (state: Write, { payload }: GetPostByIdResponseAction) => {
      return produce(state, draft => {
        draft.changed = false;
        draft.postData = payload.data;
        draft.submitBox.tags = payload.data.tags;
        draft.submitBox.url_slug = payload.data.url_slug;
        draft.body = payload.data.body;
        draft.title = payload.data.title;
        draft.thumbnail = payload.data.thumbnail;
        draft.meta = payload.data.meta;
        draft.submitBox.is_private = payload.data.is_private;
        draft.submitBox.series = payload.data.series;
      });
    },
  },
  {
    type: GET_PRODUCT_BY_ID,
    onSuccess: (state: Write, { payload }: GetPostByIdResponseAction) => {
      return produce(state, draft => {
        draft.changed = false;
        draft.postData = payload.data;
        draft.submitBox.tags = payload.data.tags;
        draft.submitBox.badges = payload.data.badges;
        draft.submitBox.url_slug = payload.data.url_slug;
        draft.body = payload.data.body;
        draft.title = payload.data.title;
        draft.thumbnail = payload.data.thumbnail;
        draft.meta = payload.data.meta;
        draft.submitBox.is_private = payload.data.is_private;
        draft.submitBox.series = payload.data.series;
      });
    },
  },
  {
    type: GET_BRAND_BY_ID,
    onSuccess: (state: Write, { payload }: GetPostByIdResponseAction) => {
      return produce(state, draft => {
        draft.changed = false;
        draft.postData = payload.data;
        draft.submitBox.tags = payload.data.tags;
        draft.submitBox.url_slug = payload.data.url_slug;
        draft.body = payload.data.body;
        draft.title = payload.data.title;
        draft.thumbnail = payload.data.thumbnail;
        draft.meta = payload.data.meta;
        draft.submitBox.is_private = payload.data.is_private;
        draft.submitBox.series = payload.data.series;
      });
    },
  },
  {
    type: GET_BANNER_BY_ID,
    onSuccess: (state: Write, { payload }: GetPostByIdResponseAction) => {
      return produce(state, draft => {
        draft.changed = false;
        draft.postData = payload.data;
        draft.submitBox.tags = payload.data.tags;
        draft.submitBox.url_slug = payload.data.url_slug;
        draft.body = payload.data.body;
        draft.title = payload.data.title;
        draft.thumbnail = payload.data.thumbnail;
        draft.meta = payload.data.meta;
        draft.submitBox.is_private = payload.data.is_private;
        draft.submitBox.series = payload.data.series;
      });
    },
  },
  {
    type: GET_EVENT_BY_ID,
    onSuccess: (state: Write, { payload }: GetPostByIdResponseAction) => {
      return produce(state, draft => {
        draft.changed = false;
        draft.postData = payload.data;
        draft.submitBox.tags = payload.data.tags;
        draft.submitBox.url_slug = payload.data.url_slug;
        draft.body = payload.data.body;
        draft.title = payload.data.title;
        draft.thumbnail = payload.data.thumbnail;
        draft.meta = payload.data.meta;
        draft.submitBox.is_private = payload.data.is_private;
        draft.submitBox.series = payload.data.series;
      });
    },
  },
  {
    type: GET_LETTER_BY_ID,
    onSuccess: (state: Write, { payload }: GetPostByIdResponseAction) => {
      return produce(state, draft => {
        draft.changed = false;
        draft.postData = payload.data;
        draft.submitBox.tags = payload.data.tags;
        draft.submitBox.url_slug = payload.data.url_slug;
        draft.body = payload.data.body;
        draft.title = payload.data.title;
        draft.thumbnail = payload.data.thumbnail;
        draft.meta = payload.data.meta;
        draft.submitBox.is_private = payload.data.is_private;
        draft.submitBox.series = payload.data.series;
      });
    },
  },
  {
    type: GET_QUOTE_BY_ID,
    onSuccess: (state: Write, { payload }: GetPostByIdResponseAction) => {
      return produce(state, draft => {
        draft.changed = false;
        draft.postData = payload.data;
        draft.submitBox.tags = payload.data.tags;
        draft.submitBox.url_slug = payload.data.url_slug;
        draft.body = payload.data.body;
        draft.title = payload.data.title;
        draft.thumbnail = payload.data.thumbnail;
        draft.meta = payload.data.meta;
        draft.submitBox.is_private = payload.data.is_private;
        draft.submitBox.series = payload.data.series;
      });
    },
  },
  {
    type: GET_PARTNER_BY_ID,
    onSuccess: (state: Write, { payload }: GetPostByIdResponseAction) => {
      return produce(state, draft => {
        draft.changed = false;
        draft.postData = payload.data;
        draft.submitBox.tags = payload.data.tags;
        draft.submitBox.url_slug = payload.data.url_slug;
        draft.body = payload.data.body;
        draft.title = payload.data.title;
        draft.thumbnail = payload.data.thumbnail;
        draft.meta = payload.data.meta;
        draft.submitBox.is_private = payload.data.is_private;
        draft.submitBox.series = payload.data.series;
      });
    },
  },
  {
    type: GET_SERIES_LIST,
    onSuccess: (state: Write, { payload }: GetSeriesResponseAction) => {
      return produce(state, draft => {
        draft.seriesModal.list = payload.data;
      });
    },
  },
]);



// WEBPACK FOOTER //
// ./src/store/modules/write.js